import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import './landing.css'

const TAILWIND_CONFIG = {
  darkMode: 'class',
  theme: {
    extend: {
      colors: {
        secondary: '#525f78',
        'surface-container-high': '#e7e7f4',
        'primary-fixed': '#dbe1ff',
        'on-tertiary-container': '#faf1ff',
        'tertiary-fixed': '#ebdcff',
        'secondary-container': '#d3e0fe',
        'on-secondary-fixed-variant': '#3a475f',
        'on-tertiary': '#ffffff',
        'inverse-primary': '#b4c5ff',
        surface: '#faf8ff',
        'on-tertiary-fixed': '#270058',
        'tertiary-fixed-dim': '#d4bbff',
        primary: '#004ccd',
        outline: '#737687',
        'error-container': '#ffdad6',
        'surface-bright': '#faf8ff',
        'on-secondary-container': '#56637c',
        'on-background': '#191b24',
        'on-secondary-fixed': '#0e1c31',
        error: '#ba1a1a',
        'surface-variant': '#e1e1ee',
        'surface-dim': '#d8d9e6',
        'primary-container': '#0f62fe',
        'on-surface-variant': '#424656',
        'on-primary-fixed-variant': '#003da9',
        'primary-fixed-dim': '#b4c5ff',
        'outline-variant': '#c3c6d8',
        'inverse-surface': '#2e303a',
        'on-primary-container': '#f3f3ff',
        'surface-tint': '#0052dd',
        'on-primary': '#ffffff',
        'on-surface': '#191b24',
        'on-error': '#ffffff',
        'surface-container-highest': '#e1e1ee',
        'secondary-fixed-dim': '#b9c7e4',
        'on-primary-fixed': '#00174c',
        'on-error-container': '#93000a',
        'secondary-fixed': '#d6e3ff',
        'inverse-on-surface': '#eff0fd',
        'on-secondary': '#ffffff',
        'tertiary-container': '#8a3ffc',
        tertiary: '#7015e2',
        'surface-container-lowest': '#ffffff',
        'surface-container': '#ecedfa',
        'surface-container-low': '#f2f3ff',
        'on-tertiary-fixed-variant': '#5d00c2',
        background: '#faf8ff',
      },
      fontFamily: {
        headline: ['Manrope'],
        body: ['Inter'],
        label: ['Inter'],
      },
      borderRadius: {
        DEFAULT: '0.25rem',
        lg: '0.5rem',
        xl: '0.75rem',
        full: '9999px',
      },
    },
  },
}

function useTailwindCDN() {
  useEffect(() => {
    const fontsLink = document.createElement('link')
    fontsLink.rel = 'stylesheet'
    fontsLink.href =
      'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&family=Manrope:wght@700;800&display=swap'
    document.head.appendChild(fontsLink)

    const iconsLink = document.createElement('link')
    iconsLink.rel = 'stylesheet'
    iconsLink.href =
      'https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:wght,FILL@100..700,0..1&display=swap'
    document.head.appendChild(iconsLink)

    const script = document.createElement('script')
    script.src = 'https://cdn.tailwindcss.com?plugins=forms,container-queries'
    script.onload = () => {
      if (window.tailwind) {
        window.tailwind.config = TAILWIND_CONFIG
      }
    }
    document.head.appendChild(script)

    return () => {
      document.head.removeChild(fontsLink)
      document.head.removeChild(iconsLink)
      document.head.removeChild(script)
    }
  }, [])
}

declare global {
  interface Window {
    tailwind?: { config: typeof TAILWIND_CONFIG }
  }
}

export default function LandingPage() {
  const navigate = useNavigate()
  useTailwindCDN()

  return (
    <div className="landing-page bg-surface text-on-surface font-body selection:bg-primary-fixed selection:text-on-primary-fixed">

      {/* TopNavBar */}
      <nav className="bg-[#faf8ff] w-full top-0 sticky z-50">
        <div className="flex justify-between items-center w-full px-8 py-4 max-w-7xl mx-auto">
          <div className="text-2xl font-black text-[#191b24] font-['Manrope'] tracking-tight">
            Digital Curator
          </div>
          <div className="flex items-center gap-8">
            <div className="hidden md:flex items-center gap-8">
              <a
                className="text-[#0F62FE] border-b-2 border-[#0F62FE] pb-1 font-['Manrope'] font-bold tracking-tight"
                href="#"
              >
                Product
              </a>
              <a
                className="text-[#424656] font-['Manrope'] font-bold tracking-tight hover:text-[#0F62FE] transition-colors duration-200"
                href="#"
              >
                Solutions
              </a>
              <a
                className="text-[#424656] font-['Manrope'] font-bold tracking-tight hover:text-[#0F62FE] transition-colors duration-200"
                href="#"
              >
                Intelligence
              </a>
              <a
                className="text-[#424656] font-['Manrope'] font-bold tracking-tight hover:text-[#0F62FE] transition-colors duration-200"
                href="#"
              >
                Pricing
              </a>
            </div>
            <button className="bg-primary hover:bg-primary-container text-white px-6 py-2.5 rounded-lg font-bold transition-all active:scale-95 shadow-sm">
              Contact Us
            </button>
          </div>
        </div>
      </nav>

      <main>
        {/* Hero Section */}
        <section className="relative pt-20 pb-32 overflow-hidden">
          <div className="max-w-7xl mx-auto px-8 grid gap-16 items-center">
            <div className="z-10 text-center">
              <span className="inline-block py-1 px-3 rounded-full bg-secondary-container text-on-secondary-container text-xs font-bold uppercase tracking-widest mb-6 mx-auto">
                AI-Powered Document Intelligence
              </span>
              <h1 className="font-headline text-6xl lg:text-7xl font-extrabold text-on-surface leading-[1.1] mb-8 tracking-tight">
                Transform Documents into{' '}
                <span className="text-primary">Intelligence.</span>
              </h1>
              <p className="text-xl text-on-surface-variant max-w-xl mb-10 leading-relaxed mx-auto">
                Stop searching and start discovering. Our AI extracts meaningful
                data, automates tagging, and provides instant answers from your
                entire document library.
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <button
                  onClick={() => navigate('/login')}
                  className="bg-gradient-to-br from-primary to-primary-container text-white px-8 py-4 rounded-lg font-bold text-lg shadow-lg hover:shadow-xl transition-all active:scale-95"
                >
                  Get Started
                </button>
                <button className="bg-surface-container-lowest text-primary px-8 py-4 rounded-lg font-bold text-lg border-2 border-outline-variant/20 hover:bg-surface-container-low transition-all active:scale-95 flex items-center gap-2">
                  <span className="material-symbols-outlined">play_circle</span>
                  Watch Demo
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* How it Works */}
        <section className="py-24 bg-surface-container-low">
          <div className="max-w-7xl mx-auto px-8">
            <div className="text-center mb-20">
              <h2 className="font-headline text-4xl font-extrabold mb-4 tracking-tight">
                Intelligent Orchestration
              </h2>
              <p className="text-on-surface-variant text-lg">
                From raw files to actionable insights in seconds.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-12">
              <div className="text-center group">
                <div className="w-16 h-16 bg-surface-container-lowest rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-sm group-hover:bg-primary-container group-hover:text-white transition-colors">
                  <span className="material-symbols-outlined text-3xl">
                    upload_file
                  </span>
                </div>
                <h3 className="font-headline text-xl font-bold mb-3">
                  1. Upload
                </h3>
                <p className="text-on-surface-variant">
                  Securely ingest PDFs, images, and documents from any source
                  including cloud storage.
                </p>
              </div>
              <div className="text-center group">
                <div className="w-16 h-16 bg-surface-container-lowest rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-sm group-hover:bg-primary-container group-hover:text-white transition-colors">
                  <span className="material-symbols-outlined text-3xl">
                    psychology
                  </span>
                </div>
                <h3 className="font-headline text-xl font-bold mb-3">
                  2. Extract
                </h3>
                <p className="text-on-surface-variant">
                  Our AI engines parse content, identifying entities, dates, and
                  complex data structures automatically.
                </p>
              </div>
              <div className="text-center group">
                <div className="w-16 h-16 bg-surface-container-lowest rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-sm group-hover:bg-primary-container group-hover:text-white transition-colors">
                  <span className="material-symbols-outlined text-3xl">
                    travel_explore
                  </span>
                </div>
                <h3 className="font-headline text-xl font-bold mb-3">
                  3. Discover
                </h3>
                <p className="text-on-surface-variant">
                  Search by meaning, not just keywords. Chat with your documents
                  to find exact answers.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Key Features */}
        <section className="py-32">
          <div className="max-w-7xl mx-auto px-8">
            <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-8">
              <div className="max-w-2xl">
                <h2 className="font-headline text-4xl lg:text-5xl font-extrabold mb-6 tracking-tight">
                  Designed for the Modern Enterprise
                </h2>
                <p className="text-lg text-on-surface-variant">
                  The Digital Curator combines editorial precision with
                  computational power.
                </p>
              </div>
              <a
                className="text-primary font-bold flex items-center gap-2 hover:underline"
                href="#"
              >
                Explore all features{' '}
                <span className="material-symbols-outlined">arrow_forward</span>
              </a>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-surface-container-lowest p-8 rounded-xl transition-all hover:bg-surface-container">
                <span className="material-symbols-outlined text-tertiary text-3xl mb-6">
                  database
                </span>
                <h3 className="font-headline text-xl font-bold mb-3">
                  AI Data Extraction
                </h3>
                <p className="text-on-surface-variant">
                  Automatically pull tabular data, signatures, and key-value
                  pairs with 99.9% accuracy.
                </p>
              </div>
              <div className="bg-surface-container-lowest p-8 rounded-xl transition-all hover:bg-surface-container">
                <span className="material-symbols-outlined text-primary text-3xl mb-6">
                  search_spark
                </span>
                <h3 className="font-headline text-xl font-bold mb-3">
                  Semantic Search
                </h3>
                <p className="text-on-surface-variant">
                  Search your repository using natural language. The AI
                  understands context and intent.
                </p>
              </div>
              <div className="bg-surface-container-lowest p-8 rounded-xl transition-all hover:bg-surface-container">
                <span className="material-symbols-outlined text-tertiary text-3xl mb-6">
                  sell
                </span>
                <h3 className="font-headline text-xl font-bold mb-3">
                  Smart Tagging
                </h3>
                <p className="text-on-surface-variant">
                  AI-generated metadata and categories keep your library
                  organized without manual effort.
                </p>
              </div>
              <div className="bg-surface-container-lowest p-8 rounded-xl transition-all hover:bg-surface-container">
                <span className="material-symbols-outlined text-primary text-3xl mb-6">
                  group
                </span>
                <h3 className="font-headline text-xl font-bold mb-3">
                  Secure Collaboration
                </h3>
                <p className="text-on-surface-variant">
                  Role-based access controls and encrypted sharing for
                  cross-departmental teams.
                </p>
              </div>
              <div className="bg-surface-container-lowest p-8 rounded-xl transition-all hover:bg-surface-container">
                <span className="material-symbols-outlined text-tertiary text-3xl mb-6">
                  grid_view
                </span>
                <h3 className="font-headline text-xl font-bold mb-3">
                  Multi-format Support
                </h3>
                <p className="text-on-surface-variant">
                  Process PDFs, Word docs, Excel files, and high-resolution
                  images seamlessly.
                </p>
              </div>
              <div className="bg-surface-container-lowest p-8 rounded-xl transition-all hover:bg-surface-container">
                <span className="material-symbols-outlined text-primary text-3xl mb-6">
                  shield
                </span>
                <h3 className="font-headline text-xl font-bold mb-3">
                  Detailed Audit Logs
                </h3>
                <p className="text-on-surface-variant">
                  Maintain compliance with comprehensive tracking of every
                  access and modification.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section className="py-24 bg-surface-container-low">
          <div className="max-w-7xl mx-auto px-8">
            <div className="text-center mb-16">
              <h2 className="font-headline text-4xl font-extrabold mb-4 tracking-tight">
                Flexible Intelligence
              </h2>
              <p className="text-on-surface-variant text-lg">
                Choose the plan that fits your document volume.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8 items-center">
              {/* Starter */}
              <div className="bg-surface-container-lowest p-10 rounded-xl flex flex-col h-full md:h-auto lg:h-[500px]">
                <h3 className="font-headline text-2xl font-bold mb-2">
                  Starter
                </h3>
                <div className="text-4xl font-extrabold mb-6">Free</div>
                <p className="text-on-surface-variant mb-8 flex-grow">
                  Perfect for individual researchers and small projects.
                </p>
                <ul className="space-y-4 mb-10">
                  <li className="flex items-center gap-3">
                    <span className="material-symbols-outlined text-primary text-sm">
                      check_circle
                    </span>{' '}
                    100 Documents / Month
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="material-symbols-outlined text-primary text-sm">
                      check_circle
                    </span>{' '}
                    Standard Search
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="material-symbols-outlined text-primary text-sm">
                      check_circle
                    </span>{' '}
                    2 Team Members
                  </li>
                </ul>
                <button className="w-full py-3 rounded-lg border-2 border-outline-variant/20 font-bold hover:bg-surface-container-low transition-all">
                  Get Started
                </button>
              </div>
              {/* Professional */}
              <div className="bg-surface-container-lowest p-10 rounded-xl flex flex-col h-full border-2 border-primary relative shadow-2xl scale-110 z-10 lg:h-[560px]">
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-primary text-white text-xs font-bold px-4 py-1 rounded-full uppercase">
                  Recommended
                </div>
                <h3 className="font-headline text-2xl font-bold mb-2">
                  Professional
                </h3>
                <div className="text-4xl font-extrabold mb-6">
                  $29
                  <span className="text-lg text-on-surface-variant font-normal">
                    /mo
                  </span>
                </div>
                <p className="text-on-surface-variant mb-8 flex-grow">
                  Advanced AI features for growing teams and active departments.
                </p>
                <ul className="space-y-4 mb-10">
                  <li className="flex items-center gap-3">
                    <span className="material-symbols-outlined text-primary text-sm">
                      check_circle
                    </span>{' '}
                    Unlimited Documents
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="material-symbols-outlined text-primary text-sm">
                      check_circle
                    </span>{' '}
                    Advanced Semantic Search
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="material-symbols-outlined text-primary text-sm">
                      check_circle
                    </span>{' '}
                    Custom AI Extraction Models
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="material-symbols-outlined text-primary text-sm">
                      check_circle
                    </span>{' '}
                    Priority Support
                  </li>
                </ul>
                <button className="w-full py-4 rounded-lg bg-primary text-white font-bold shadow-lg shadow-primary/20 hover:bg-primary-container transition-all">
                  Subscribe Now
                </button>
              </div>
              {/* Enterprise */}
              <div className="bg-surface-container-lowest p-10 rounded-xl flex flex-col h-full md:h-auto lg:h-[500px]">
                <h3 className="font-headline text-2xl font-bold mb-2">
                  Enterprise
                </h3>
                <div className="text-4xl font-extrabold mb-6">Custom</div>
                <p className="text-on-surface-variant mb-8 flex-grow">
                  Full control and dedicated intelligence for massive scale.
                </p>
                <ul className="space-y-4 mb-10">
                  <li className="flex items-center gap-3">
                    <span className="material-symbols-outlined text-primary text-sm">
                      check_circle
                    </span>{' '}
                    On-premise Deployment
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="material-symbols-outlined text-primary text-sm">
                      check_circle
                    </span>{' '}
                    Unlimited Users
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="material-symbols-outlined text-primary text-sm">
                      check_circle
                    </span>{' '}
                    API Access &amp; Webhooks
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="material-symbols-outlined text-primary text-sm">
                      check_circle
                    </span>{' '}
                    Dedicated Account Manager
                  </li>
                </ul>
                <button className="w-full py-3 rounded-lg border-2 border-outline-variant/20 font-bold hover:bg-surface-container-low transition-all">
                  Contact Sales
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="bg-[#0F62FE] py-32 px-8">
          <div className="max-w-5xl mx-auto text-center text-white">
            <h2 className="font-headline text-4xl lg:text-6xl font-extrabold mb-8">
              Ready to curate your digital brain?
            </h2>
            <p className="text-xl lg:text-2xl opacity-90 mb-12 max-w-2xl mx-auto">
              Join thousands of organizations transforming their document chaos
              into competitive intelligence.
            </p>
            <button
              onClick={() => navigate('/login')}
              className="bg-white text-[#0F62FE] px-12 py-5 rounded-lg font-bold text-xl shadow-xl hover:bg-primary-fixed transition-all active:scale-95"
            >
              Start Your Free Trial
            </button>
            <p className="mt-8 text-sm opacity-75">
              No credit card required. 14-day full feature access.
            </p>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-[#f2f3ff] w-full">
        <div className="flex flex-col md:flex-row justify-between items-center w-full px-8 py-12 max-w-7xl mx-auto gap-4">
          <div className="font-['Manrope'] font-extrabold text-[#191b24] text-xl">
            Digital Curator
          </div>
          <div className="flex flex-wrap justify-center gap-6">
            <a
              className="text-[#424656] font-['Inter'] text-sm hover:underline hover:text-[#0F62FE] transition-colors duration-200"
              href="#"
            >
              Privacy Policy
            </a>
            <a
              className="text-[#424656] font-['Inter'] text-sm hover:underline hover:text-[#0F62FE] transition-colors duration-200"
              href="#"
            >
              Terms of Service
            </a>
            <a
              className="text-[#424656] font-['Inter'] text-sm hover:underline hover:text-[#0F62FE] transition-colors duration-200"
              href="#"
            >
              Security
            </a>
            <a
              className="text-[#424656] font-['Inter'] text-sm hover:underline hover:text-[#0F62FE] transition-colors duration-200"
              href="#"
            >
              Status
            </a>
            <a
              className="text-[#424656] font-['Inter'] text-sm hover:underline hover:text-[#0F62FE] transition-colors duration-200"
              href="#"
            >
              API Docs
            </a>
          </div>
          <div className="text-[#424656] font-['Inter'] text-sm">
            © 2024 Digital Curator. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  )
}
